import { ShieldCheckIcon } from 'lucide-react'
import { TaxInfoFormCard } from '@/components/TaxInfoFormCard'
import { getEmployeeTaxInfoApi, updateEmployeeTaxInfoApi } from '@/lib/api'

/**
 * Employer-managed employee tax information card (compliance path —
 * DESIGN_TaxFilingProvision.md §3.2). Lets an employer view (masked) and
 * enter/correct a same-org employee's tax residency + identifiers. This is
 * the primary entry path feeding the annual tax data package; the employee
 * self-service card (TaxInfoCard) is the convenience edit.
 * Shared implementation: TaxInfoFormCard.
 */
export function EmployeeTaxInfoCard({ userId }: { userId: number }) {
  return (
    <TaxInfoFormCard
      fetchTaxInfo={() => getEmployeeTaxInfoApi(userId)}
      saveTaxInfo={(payload) => updateEmployeeTaxInfoApi(userId, payload)}
      reloadKey={userId}
      idPrefix="emp-tax"
      copy={{
        loadErrorMessage: 'Failed to load employee tax information',
        selectCountryMessage: 'Select the tax residency country',
        successMessage: 'Employee tax information updated',
        taxIdPlaceholder: 'Enter full identifier; only last 4 shown',
        taxIdHelper: 'Enter the full identifier. Only the last 4 characters are ever displayed.',
        emptyState:
          'No tax information recorded for this employee yet. Add their tax residency and ' +
          'identifier so they are included in the annual tax data package.',
        viewNote: (
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground sm:col-span-2">
            <ShieldCheckIcon className="size-3.5 shrink-0" />
            Identifiers are masked. This data feeds the annual tax data package your organization
            uses for the employee tax declaration.
          </p>
        ),
      }}
    />
  )
}
